// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import { xIsInitialVariantSelectionPending } from "@awc/App/Store/Selectors/ProductSelectors";
import ProductDetailsVariantCustomView from "@awc/App/Widgets/ProductDetails/ProductDetailsVariantCustomView";
import useFilteredVariantTraits from "@insite/client-framework/Common/Hooks/useFilteredVariantTraits";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import updateVariantSelection from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateVariantSelection";
import { VariantDisplayTypeValues, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductDetailsVariantButton from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantButton";
import ProductDetailsVariantDropdown from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantDropdown";
import ProductDetailsVariantSwatchDropdown from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantSwatchDropdown";
import ProductDetailsVariantSwatchGrid from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantSwatchGrid";
import ProductDetailsVariantSwatchList from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantSwatchList";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasParentProductId & { isBargainBinPage: boolean };

const mapStateToProps = (state: CustomAppState, ownProps: any) => {
    // XNG-Changes
    const variantChildren = getVariantChildrenDataView(state, ownProps.parentProductId).value;
    const variantTraits = getProductState(state, ownProps.parentProductId).value?.variantTraits;

    const parentProduct = getProductState(state, ownProps.parentProductId).value;
    const availableTraitValueIds = new Set<string>();
    const quotedTraitValueIds = new Set<string>();
    const colorTraitId = variantTraits?.find(variantTrait => variantTrait.name.toLowerCase() === "color")?.id;

    if (colorTraitId) {
        variantChildren?.forEach(child => {
            const pricing = state.pages.productDetails.productInfosById?.[child.id]?.pricing;
            const traitValueId = child.childTraitValues?.find(
                (childTraitValue: any) => childTraitValue.styleTraitId === colorTraitId,
            )?.id;

            if (!traitValueId || child.isDiscontinued) {
                return;
            }

            const isQuoteRequired = parentProduct?.quoteRequired || child?.quoteRequired;
            if (!isQuoteRequired && pricing && pricing.unitNetPrice > 0) {
                availableTraitValueIds.add(traitValueId);
            } else {
                quotedTraitValueIds.add(traitValueId);
            }
        });
    }

    return {
        // XNG-Changes
        // variantTraits: getProductState(state, ownProps.parentProductId).value?.variantTraits,
        // variantChildren: getVariantChildrenDataView(state, ownProps.parentProductId).value,
        variantTraits,
        variantChildren,
        variantSelection: state.pages.productDetails.variantSelection,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        variantProductState: getProductState(state, state.pages.productDetails.selectedProductId),
        selectedProductId: state.pages.productDetails.selectedProductId,
        // XNG-Change
        availableTraitValueIds,
        quotedTraitValueIds,
        isVariantSelectionPending: xIsInitialVariantSelectionPending(state, parentProduct),
    };
};

const mapDispatchToProps = {
    updateVariantSelection,
};

export interface ProductDetailsVariantOptionsStyles {
    wrapper?: InjectableCss;
}

export const variantOptionsStyles: ProductDetailsVariantOptionsStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = variantOptionsStyles;

const ProductDetailsVariantOptions: React.FC<Props> = ({
    variantChildren,
    variantSelection,
    updateVariantSelection,
    variantTraits,
    parentProductId,
    variantProductState,
    selectedProductId,
    // XNG-Change
    isBargainBinPage,
    availableTraitValueIds,
    quotedTraitValueIds,
    isVariantSelectionPending,
}) => {
    useEffect(() => {
        let newUrl = window.location.href;
        const optionIndex = newUrl.indexOf("option");
        if (optionIndex >= 0) {
            newUrl = window.location.href.substring(0, optionIndex - 1);
        }
        if (variantProductState.value && variantProductState.value.isVariantParent) {
            window.history.replaceState(null, "", newUrl);
        } else if (variantProductState.value && variantProductState.value.childTraitValues) {
            const separator = newUrl.indexOf("?") >= 0 ? "&" : "?";
            newUrl = newUrl.concat(`${separator}option=`, encodeURIComponent(variantProductState.value.productNumber));
            window.history.replaceState(null, "", newUrl);
        }
    }, [selectedProductId]);

    // XNG-Change: send isBargainBinPage
    const variantChangeHandler = (traitValueId: string, variantTraitId: string) => {
        updateVariantSelection({ traitValueId, variantTraitId, productId: parentProductId, isBargainBinPage } as any);
    };

    if (!variantTraits || variantTraits.length === 0 || !variantChildren) {
        return null;
    }

    const filteredVariantTraits = useFilteredVariantTraits(variantTraits, variantChildren, variantSelection, true);

    const renderVariant = (variantTrait: VariantTraitModel) => {
        // XNG-Changes
        if (!isBargainBinPage && variantTrait.name.toLowerCase() === "color") {
            return (
                <ProductDetailsVariantCustomView
                    key={variantTrait.id}
                    variantSelection={variantSelection}
                    updateVariantSelection={variantChangeHandler}
                    variantTrait={variantTrait}
                    // XNG-Change
                    availableTraitValueIds={availableTraitValueIds}
                    quotedTraitValueIds={quotedTraitValueIds}
                    isPricingLoading={isVariantSelectionPending}
                />
            );
        }

        switch (variantTrait.displayType) {
            case VariantDisplayTypeValues.Button:
                return (
                    <ProductDetailsVariantButton
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
            case VariantDisplayTypeValues.SwatchDropdown:
                return (
                    <ProductDetailsVariantSwatchDropdown
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
            case VariantDisplayTypeValues.SwatchGrid:
                return (
                    <ProductDetailsVariantSwatchGrid
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
            case VariantDisplayTypeValues.SwatchList:
                return (
                    <ProductDetailsVariantSwatchList
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
            default:
                return (
                    <ProductDetailsVariantDropdown
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
        }
    };

    return (
        <StyledWrapper {...styles.wrapper} className="variants-wrapper">
            {filteredVariantTraits.map(variantTrait => {
                // XNG-Changes: Filter out disabled traitValues
                const enabledTraitValues = variantTrait.traitValues?.filter(traitValue => !traitValue.isDisabled);

                return enabledTraitValues?.length
                    ? renderVariant({
                          ...variantTrait,
                          traitValues: enabledTraitValues,
                      } as VariantTraitModel)
                    : null;
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsVariantOptions)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
